/**
 * EQ API - EQ相关后端接口
 * 对齐旧版 js/eq_setting.js
 * 后端域名: https://www.aiiifly.com（本地开发经 Vite 代理 /api）
 */

import type { EqDataPoint } from './device/commands'
import { API_BASE_URL } from '@/config/api'

export type EqMode = 'game' | 'music' | 'movie'

export interface EqPresetData {
  id: string
  name: string
  datapoint: EqDataPoint[]
  isCustom?: boolean
  createdAt?: number
}

export interface EqListResponse {
  presets: EqPresetData[]
  lastPreset: string
  defaultPreset: string
}

export interface ApiResponse<T = unknown> {
  code: number
  message: string
  data: T
}

// EQ API endpoint mapping
const EQ_API_MAP: Record<EqMode, {
  list: string
  add: string
  remove: string
  pin: string
  find: string
  reset: string
  updatePoint: string
}> = {
  game: {
    list: '/game/list',
    add: '/game/addEqData',
    remove: '/game/deleteEqData',
    pin: '/game/setTop',
    find: '/game/findEqById',
    reset: '/game/resetEqData',
    updatePoint: '/game/updatePointByFid',
  },
  music: {
    list: '/music/list',
    add: '/music/addEqData',
    remove: '/music/deleteEqData',
    pin: '/music/setTop',
    find: '/music/findEqById',
    reset: '/music/resetEqData',
    updatePoint: '/music/updatePointByFid',
  },
  movie: {
    list: '/cinema/list',
    add: '/cinema/addEqData',
    remove: '/cinema/deleteEqData',
    pin: '/cinema/setTop',
    find: '/cinema/findEqById',
    reset: '/cinema/resetEqData',
    updatePoint: '/cinema/updatePointByFid',
  },
}

/**
 * Get EQ list for a mode
 * POST /api/{mode}/list
 */
export async function getEqList(mode: EqMode, uuid: string): Promise<ApiResponse<EqListResponse>> {
  const formData = new FormData()
  formData.append('uuid', uuid)

  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode].list}`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

/**
 * Add custom EQ preset
 * POST /api/{mode}/addEqData
 */
export async function addEqPreset(
  mode: EqMode,
  uuid: string,
  preset: EqPresetData,
): Promise<ApiResponse<{ id: string }>> {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('name', preset.name)
  formData.append('datapoint', JSON.stringify(preset.datapoint))

  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode].add}`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

/**
 * Remove EQ preset
 * POST /api/{mode}/deleteEqData
 */
export async function removeEqPreset(
  mode: EqMode,
  uuid: string,
  presetId: string,
): Promise<ApiResponse> {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('presetId', presetId)

  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode].remove}`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

/**
 * Pin/置顶 EQ preset
 * POST /api/{mode}/setTop
 */
export async function pinEqPreset(
  mode: EqMode,
  uuid: string,
  presetId: string,
): Promise<ApiResponse> {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('presetId', presetId)

  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode].pin}`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

/**
 * Find EQ preset by ID
 * POST /api/{mode}/findEqById
 */
export async function findEqById(
  mode: EqMode,
  uuid: string,
  presetId: string,
): Promise<ApiResponse<EqPresetData>> {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('presetId', presetId)

  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode].find}`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

/**
 * Reset EQ preset to default
 * POST /api/{mode}/resetEqData
 */
export async function resetEqPreset(
  mode: EqMode,
  uuid: string,
  presetId: string,
): Promise<ApiResponse> {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('presetId', presetId)

  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode].reset}`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

/**
 * Update single EQ point
 * POST /api/{mode}/updatePointByFid
 */
export async function updateEqPoint(
  mode: EqMode,
  uuid: string,
  presetId: string,
  point: EqDataPoint,
): Promise<ApiResponse> {
  const formData = new FormData()
  formData.append('uuid', uuid)
  formData.append('presetId', presetId)
  formData.append('fid', String(point.fid))
  formData.append('newFreq', point.freq)
  formData.append('newGain', String(point.gain))

  const response = await fetch(`${API_BASE_URL}${EQ_API_MAP[mode].updatePoint}`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}
